import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { loucaApi } from '../services/loucaApi';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';

const InspectionUpload = () => {
  const [videos, setVideos] = useState([null, null, null, null]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const [testPrompt, setTestPrompt] = useState('Raconte moi une histoire');
  const [testResponse, setTestResponse] = useState('');
  const [testMetadata, setTestMetadata] = useState(null);
  const [isTestingPrompt, setIsTestingPrompt] = useState(false);
  const [testPromptError, setTestPromptError] = useState(null);
  const [aiEngines, setAiEngines] = useState([]);
  const [selectedAiEngine, setSelectedAiEngine] = useState('');
  const [aiLoading, setAiLoading] = useState(true);
  const [aiError, setAiError] = useState(null);
  const { user } = useAuth();
  const { selectedVersion, setSelectedVersion, availableVersions } = useConfig();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    const fetchEngines = async () => {
      try {
        const response = await loucaApi.getAIEngines();
        if (!mounted) return;
        const engines = response?.data?.engines || [];
        setAiEngines(engines);
      } catch (err) {
        if (mounted) {
          console.error('Failed to load AI engines', err);
          setAiError("Impossible de charger la liste des IA disponibles.");
        }
      } finally {
        if (mounted) {
          setAiLoading(false);
        }
      }
    };
    fetchEngines();
    return () => {
      mounted = false;
    };
  }, []);

  const compatibleAiEngines = useMemo(
    () => aiEngines.filter((engine) => (engine.workflows || []).includes(selectedVersion)),
    [aiEngines, selectedVersion]
  );

  useEffect(() => {
    if (!compatibleAiEngines.length) {
      setSelectedAiEngine('');
      return;
    }
    setSelectedAiEngine((current) =>
      compatibleAiEngines.some((engine) => engine.id === current)
        ? current
        : compatibleAiEngines[0].id
    );
  }, [compatibleAiEngines]);

  const videoLabels = [
    'Extérieur (obligatoire)',
    'Coffre',
    'Intérieur',
    'Tableau de bord',
  ];

  const handleVideoChange = (index, file) => {
    const newVideos = [...videos];
    newVideos[index] = file;
    setVideos(newVideos);
  };

  const generateInspectionId = (prefix = 'INS') => {
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '_');
    const userId = user?.user_id || 'DRIVER';
    return `${prefix}_${timestamp}_${userId}`;
  };

  const handlePromptTest = async () => {
    const promptValue = (testPrompt || '').trim() || 'Raconte moi une histoire';
    setIsTestingPrompt(true);
    setTestPromptError(null);
    setTestResponse('');
    setTestMetadata(null);

    try {
      const response = await loucaApi.testPrompt({ prompt: promptValue });
      if (response.status !== 'success') {
        throw new Error(response.message || 'Erreur lors du test LLM');
      }

      const payload = response.data || {};
      setTestResponse(payload.response || '');
      setTestMetadata({
        model: payload.model,
        durationMs: payload.duration_ms,
      });
    } catch (err) {
      const message = err?.response?.data?.detail?.error?.message || err?.message || 'Erreur lors du test LLM';
      setTestPromptError(message);
    } finally {
      setIsTestingPrompt(false);
    }
  };

  const isVideoComplex = selectedVersion === 'video_complex_v1';
  const isVideoBenchmark = selectedVersion === 'video_benchmark_v1';
  const isImagesWorkflow = selectedVersion === 'images_to_ia_v1';
  const isCars2025 = selectedVersion === 'cars2025_yolo_v1';
  const isSingleVideoWorkflow = isImagesWorkflow || isCars2025;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!videos[0]) {
      setError('La vidéo extérieure est obligatoire');
      return;
    }

    // Pour YOLO 2025, pas de sélection IA requise (moteur local)
    if (!isCars2025) {
      if (!selectedAiEngine) {
        setError("Aucun moteur IA compatible n'est disponible pour ce workflow.");
        return;
      }
    }

    setUploading(true);
    setError(null);

    const ownerPayload = { type: 'driver', id: user?.user_id || 'DRIVER' };
    const generatedId = isImagesWorkflow
      ? generateInspectionId('INS02_EXTERIOR')
      : isVideoBenchmark
      ? generateInspectionId('INS2')
      : generateInspectionId();

    const runUpload = async () => {
      if (isImagesWorkflow || isCars2025) {
        const exteriorVideo = videos[0];
        const formData = new FormData();
        formData.append('inspection_id', generatedId);
        formData.append('user_id', ownerPayload.id);
        formData.append('owner', JSON.stringify(ownerPayload));
        formData.append('version', selectedVersion);
        if (!isCars2025 && selectedAiEngine) {
          formData.append('ai_engine_id', selectedAiEngine);
        }
        if (comment.trim()) {
          formData.append('comment', comment.trim().slice(0, 50));
        }
        formData.append('video', exteriorVideo);

        const response = await loucaApi.analyzeExterior(formData);

        if (response.status !== 'success') {
          throw new Error(response.message || 'Erreur lors du lancement de l’analyse');
        }

        return response.data?.inspection_id || generatedId;
      }

      if (isVideoBenchmark) {
        const formData = new FormData();
        formData.append('inspection_id', generatedId);
        formData.append('user_id', ownerPayload.id);
        formData.append('owner', JSON.stringify(ownerPayload));
        formData.append('timestamp', new Date().toISOString());
        formData.append('ai_engine_id', selectedAiEngine);
        if (comment.trim()) {
          formData.append('comment', comment.trim().slice(0, 50));
        }

        videos.forEach((video) => {
          if (video) {
            formData.append('videos', video);
          }
        });

        const response = await loucaApi.createInspectionSimple(formData);
        const inspectionId = response.data?.inspection_id || generatedId;

        if (response.status !== 'success') {
          throw new Error(response.message || "Erreur lors de l'upload (prompt simple)");
        }

        return inspectionId;
      }

      const formData = new FormData();
      formData.append('inspection_id', generatedId);
      formData.append('user_id', ownerPayload.id);
      formData.append('owner', JSON.stringify(ownerPayload));
      formData.append('timestamp', new Date().toISOString());
      formData.append('ai_engine_id', selectedAiEngine);
      if (comment.trim()) {
        formData.append('comment', comment.trim().slice(0, 50));
      }

      videos.forEach((video) => {
        if (video) {
          formData.append('videos', video);
        }
      });

      const response = await loucaApi.createInspection(formData);
      const inspectionId = response.data?.inspection_id || generatedId;

      if (response.status !== 'success') {
        throw new Error(response.message || "Erreur lors de l'upload");
      }

      return inspectionId;
    };

    try {
      const inspectionId = await runUpload();
      const replaceHistory = inspectionId !== generatedId;
      setUploading(false);
      navigate(`/inspection/${inspectionId}`, {
        state: { status: 'processing', optimistic: inspectionId === generatedId },
        replace: replaceHistory,
      });
    } catch (err) {
      console.error('Upload error:', err);
      const message = err?.message || "Erreur lors de l'upload";
      sessionStorage.setItem('louca_last_upload_error', message);
      setError(message);
      setUploading(false);
      return;
    }
  };

  return (
    <Layout>
      <div className="pb-24 max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-6">Nouvelle Inspection</h1>

        <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-5">
          <h2 className="text-lg font-semibold text-white mb-2">Configuration active</h2>
          <p className="text-sm text-slate-300">
            {isImagesWorkflow
              ? 'Workflow Images → IA : une seule vidéo extérieure, extraction des images clés puis analyse Vision + LLM.'
              : isVideoBenchmark
              ? 'Workflow Video → IA Bench : benchmark basé sur le prompt historique, jusqu’à 4 vidéos.'
              : isCars2025
              ? 'Workflow YOLO 2025 : une seule vidéo extérieure, extraction adaptative puis détection Cars2025 (détourage + synthèse).'
              : 'Workflow Video → IA (prompt complexe) : jusqu’à 4 vidéos pour couvrir tous les angles, prompt optimisé.'}
          </p>
        </div>

        <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-5">
          <label className="block text-white font-semibold mb-2">Version d’analyse</label>
          <select
            value={selectedVersion}
            onChange={(e) => setSelectedVersion(e.target.value)}
            disabled={uploading}
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {availableVersions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Test rapide du LLM</h2>
              <p className="text-sm text-slate-300">
                Envoyez un prompt simple pour vérifier la disponibilité de Gemini avant une inspection.
              </p>
            </div>
            <button
              type="button"
              onClick={handlePromptTest}
              disabled={isTestingPrompt}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition disabled:opacity-50"
            >
              {isTestingPrompt ? 'Test...' : 'Tester Gemini'}
            </button>
          </div>
          <textarea
            className="mt-4 w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
            rows={3}
            value={testPrompt}
            onChange={(e) => setTestPrompt(e.target.value)}
            disabled={isTestingPrompt}
          />
          {testPromptError && (
            <div className="mt-3 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
              {testPromptError}
            </div>
          )}
          {testResponse && (
            <div className="mt-3 bg-slate-900 border border-slate-700 rounded-lg p-4">
              <div className="text-xs text-slate-400 mb-2">
                {testMetadata?.model && <span>Modèle : {testMetadata.model}</span>}
                {testMetadata?.durationMs !== undefined && (
                  <span className="ml-4">Temps : {testMetadata.durationMs} ms</span>
                )}
              </div>
              <pre className="text-sm text-slate-100 whitespace-pre-wrap break-words">
                {testResponse}
              </pre>
            </div>
          )}
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 mb-6">
          <label className="block text-white font-semibold mb-2" htmlFor="inspection-comment">
            Commentaire (50 caractères max)
          </label>
          <input
            id="inspection-comment"
            type="text"
            maxLength={50}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Ex: véhicule très sale côté droit"
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
            disabled={uploading}
          />
        </div>

        {!isCars2025 && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 mb-6">
            <label className="block text-white font-semibold mb-2">
              Moteur IA utilisé pour ce workflow
            </label>
            {aiLoading ? (
              <p className="text-sm text-slate-400">Chargement des moteurs disponibles…</p>
            ) : compatibleAiEngines.length === 0 ? (
              <p className="text-sm text-orange-300">
                Aucun moteur n’est disponible pour ce workflow. Merci de réessayer plus tard.
              </p>
            ) : (
              <select
                value={selectedAiEngine}
                onChange={(e) => setSelectedAiEngine(e.target.value)}
                disabled={uploading}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {compatibleAiEngines.map((engine) => (
                  <option key={engine.id} value={engine.id}>
                    {engine.label} • {engine.provider}
                  </option>
                ))}
              </select>
            )}
            {aiError && (
              <p className="mt-2 text-sm text-red-400">
                {aiError}
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {videoLabels.map((label, index) => {
            if (isSingleVideoWorkflow && index > 0) {
              return null;
            }

            return (
              <div key={index} className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                <label className="block text-white font-semibold mb-3">
                  {label} {index === 0 && <span className="text-red-400">*</span>}
                </label>
                <input
                  type="file"
                  accept="video/mp4,video/mov,video/avi"
                  onChange={(e) => handleVideoChange(index, e.target.files[0])}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                />
                {videos[index] && (
                  <p className="mt-2 text-sm text-slate-400">
                    {videos[index].name} ({(videos[index].size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>
            );
          })}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/inspections')}
              disabled={uploading}
              className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={
                uploading ||
                !videos[0] ||
                (!isCars2025 && (aiLoading || !selectedAiEngine))
              }
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
            >
              {uploading ? 'Envoi en cours...' : "Commencer l'inspection"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default InspectionUpload;



